import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { Identifier, XYCoord } from 'dnd-core';

export interface DraggableColumnProps {
    id: any;
    column: string;
    index: number;
    moveCol: (dragIndex: number, hoverIndex: number) => void;
    removeCol: (col: string) => void;
}
export function DraggableColumn({ id, column, index, moveCol, removeCol }: DraggableColumnProps) {
    const dragRef = React.useRef<HTMLDivElement>(null);
    const previewRef = React.useRef<HTMLDivElement>(null);
    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
        accept: 'column',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!previewRef.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = previewRef.current?.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            moveCol(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'column',
        item: () => {
            return { id, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(dragRef);
    drop(preview(previewRef));

    return (
        <div
            className={`ordered-column border rounded my-2 ${isDragging && 'is-dragging'}`}
            ref={previewRef}
            data-handler-id={handlerId}
        >
            <i title="drag icon" className="fa-solid fa-lg fa-bars cursor-grab p-3" ref={dragRef} />
            <span>{column}</span>
            <button
                type="button"
                onClick={() => removeCol(column)}
                aria-label={`Remove ${column}`}
                className="pull-right text-danger fw-bold link-text ms-auto me-3"
            >
                x
            </button>
        </div>
    );
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}
